import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DjayToetsAvatar } from '../../mascot';
import type { AssessmentReport } from '../types';
interface AssignedAssessmentCompleteScreenProps {
  report: AssessmentReport;
  onExit: () => void;
}
export function AssignedAssessmentCompleteScreen({
  report,
  onExit
}: AssignedAssessmentCompleteScreenProps) {
  const totalQuestions = report.score.totalQuestions;
  const correctCount = report.score.correctCount;
  const score = report.score.percentage;
  const answers = report.answers ?? [];
  return <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex items-center justify-center p-4" data-id="element-1070" data-mp="30kc7d64okzj4">
      <motion.div initial={{
      opacity: 0,
      scale: 0.9,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="w-full max-w-md" data-id="element-1071" data-mp="35lrxhclptw8d">
        
        <Card className="p-8 text-center shadow-lg border-orange-100" data-id="element-1072" data-mp="2b088867jelld">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          delay: 0.2,
          type: 'spring',
          stiffness: 200,
          damping: 15
        }} className="mb-6" data-id="element-1073" data-mp="2b2b436vdf8y0">
            
            <DjayToetsAvatar domain="toets" size="lg" animate data-id="element-1074" data-mp="2yord0brdog8p" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} data-id="element-1075" data-mp="2tq6eqhvos6mw">
            
            <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-4" data-id="element-1076" data-mp="2k1ofw66yw9lw">
              <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-1077" data-mp="3hhc1lh01nz6p" />
            </div>

            <h1 className="text-2xl font-black text-slate-900 mb-2" data-id="element-1078" data-mp="3cp285qzq0gzo">
              Toets klaar! 🎉
            </h1>
            <p className="text-slate-600 mb-6" data-id="element-1079" data-mp="1at32xahacz0l">
              Goed gedaan! Je hebt de toets afgerond.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} className="bg-slate-50 rounded-xl p-4 mb-4" data-id="element-1080" data-mp="1ft7gosqt7fn4">
            
            <div className="grid grid-cols-2 gap-4" data-id="element-1081" data-mp="s1aii4ga8m4h">
              <div data-id="element-1082" data-mp="s3mwk6ix6cc9">
                <div className="text-2xl font-black text-orange-600" data-id="element-1083" data-mp="w2vsni1fmmaz">
                  {Math.round(score)}%
                </div>
                <div className="text-xs font-medium text-slate-500 mt-0.5" data-id="element-1084" data-mp="i4e9zyqdvzzx">
                  Score
                </div>
              </div>
              <div data-id="element-1085" data-mp="n4jtusqewi7g">
                <div className="text-2xl font-black text-slate-800" data-id="element-1086" data-mp="2chdsq0q29abf">
                  {correctCount}/{totalQuestions}
                </div>
                <div className="text-xs font-medium text-slate-500 mt-0.5" data-id="element-1087" data-mp="2hff9gf00kxjq">
                  Goed beantwoord
                </div>
              </div>
            </div>
          </motion.div>

          {/* Per-question goed/fout summary */}
          {answers.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.7
        }} className="mb-6" data-id="element-1088" data-mp="fiodb0yxg60p">
            
              <h2 className="text-sm font-bold text-slate-700 mb-3 text-left" data-id="element-1089" data-mp="oitj56oh2wcr">
                Overzicht per vraag
              </h2>
              <div className="grid grid-cols-5 sm:grid-cols-6 gap-2" data-id="element-1090" data-mp="jk9wt55yvijv">
                {answers.map((answer, index) => <motion.div key={answer.questionId} initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.75 + index * 0.03
            }} className={`
                      flex flex-col items-center justify-center rounded-lg py-2 px-1
                      ${answer.isCorrect ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}
                    `} data-id="element-1091" data-mp="yh0xdhrdn0b2" data-mpi={index}>
                
                    <span className="text-xs font-bold mb-0.5" data-id="element-1092" data-mp="1re1t8rzgis19">
                      {index + 1}
                    </span>
                    {answer.isCorrect ? <CheckCircle2 className="w-4 h-4" data-id="element-1093" data-mp="20bvl9qq6uzim" /> : <XCircle className="w-4 h-4" data-id="element-1094" data-mp="20asvxn8il54t" />}
                  </motion.div>)}
              </div>
            </motion.div>}

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.9
        }} data-id="element-1095" data-mp="18lqv1bfqzi9h">
            
            <p className="text-sm text-slate-500 mb-5" data-id="element-1096" data-mp="1cm1arftcdn1j">
              Je docent kan de resultaten bekijken in het leerkrachtportaal.
            </p>

            <Button onClick={onExit} className="w-full bg-orange-500 hover:bg-orange-600 text-white shadow-sm" size="lg" data-id="element-1097" data-mp="kwoey80rhxh3">
              
              Terug naar home
              <ArrowRight className="w-4 h-4 ml-2" data-id="element-1098" data-mp="ovdofpioiuvg" />
            </Button>
          </motion.div>
        </Card>
      </motion.div>
    </div>;
}